import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import API_URL from '../config/api';
import { useAuth } from '../context/AuthContext';
import { apiFetch } from '../utils/apiFetch';

const ChangePassword: React.FC = () => {
  const navigate = useNavigate();
  const auth = useAuth();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!currentPassword || !newPassword || !confirmPassword) {
      return setError('All fields are required');
    }
    if (newPassword.length < 8) {
      return setError('New password must be at least 8 characters');
    }
    if (newPassword !== confirmPassword) {
      return setError('New passwords do not match');
    }

    setLoading(true);
    try {
      const token = auth.token;
      const res = await apiFetch(`${API_URL}/auth/change-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ currentPassword, newPassword }),
      });
      const data = await res.json();
      if (res.ok) {
        navigate('/dashboard');
      } else {
        setError(data.message || 'Failed to change password');
      }
    } catch {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={pageContainer}>
      <div style={card}>
        <h1 style={title}>Change Your Password</h1>
        <p style={subtitle}>
          You must set a new password before continuing.
          This is a one-time requirement.
        </p>

        <form onSubmit={handleSubmit} style={formStyle}>
          <div style={fieldGroup}>
            <label style={labelStyle}>Current Password</label>
            <input
              style={inputStyle}
              type="password"
              placeholder="Enter your temporary password"
              value={currentPassword}
              onChange={e => setCurrentPassword(e.target.value)}
              required
              autoFocus
            />
          </div>

          <div style={fieldGroup}>
            <label style={labelStyle}>New Password</label>
            <input
              style={inputStyle}
              type="password"
              placeholder="Minimum 8 characters"
              value={newPassword}
              onChange={e => setNewPassword(e.target.value)}
              required
            />
          </div>

          <div style={fieldGroup}>
            <label style={labelStyle}>Confirm New Password</label>
            <input
              style={inputStyle}
              type="password"
              placeholder="Re-enter new password"
              value={confirmPassword}
              onChange={e => setConfirmPassword(e.target.value)}
              required
            />
          </div>

          {error && <div style={errorStyle}>{error}</div>}

          <button type="submit" style={blueButton} disabled={loading}>
            {loading ? 'Saving...' : 'Set New Password'}
          </button>
        </form>
      </div>
    </div>
  );
};

const pageContainer: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  minHeight: '100vh',
  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  padding: '20px',
};

const card: React.CSSProperties = {
  background: '#ffffff',
  borderRadius: 12,
  padding: '40px 32px',
  maxWidth: 420,
  width: '100%',
  boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
};

const title: React.CSSProperties = {
  fontSize: 28,
  fontWeight: 'bold',
  color: '#333',
  textAlign: 'center',
  marginBottom: 8,
};

const subtitle: React.CSSProperties = {
  fontSize: 15,
  color: '#666',
  textAlign: 'center',
  marginBottom: 24,
  lineHeight: 1.5,
};

const formStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 16,
};

const fieldGroup: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  textAlign: 'left',
};

const labelStyle: React.CSSProperties = {
  fontSize: 14,
  fontWeight: 600,
  color: '#333',
  marginBottom: 6,
};

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '12px 14px',
  fontSize: 15,
  border: '1px solid #ddd',
  borderRadius: 6,
  outline: 'none',
  boxSizing: 'border-box',
};

const errorStyle: React.CSSProperties = {
  color: '#dc3545',
  fontSize: 14,
  fontWeight: 600,
  textAlign: 'center',
};

const blueButton: React.CSSProperties = {
  width: '100%',
  padding: '12px 20px',
  fontSize: 16,
  fontWeight: 'bold',
  color: '#fff',
  backgroundColor: '#007bff',
  border: 'none',
  borderRadius: 6,
  cursor: 'pointer',
  marginTop: 8,
};

export default ChangePassword;
